import React, {useMemo, KeyboardEvent} from 'react';
import direction from '../../styles/direction.module.scss';
import {isValid, strToFloat} from '@priority-software/priority-utils/lib/number';
import {StringInputProps} from './StringInput';
import {
    countDecimals,
    getDecimalSign,
    getThousandSign,
    getMaxNumberLength
} from '../../utils/number';
import {keys} from '../../../../constants/KeyCodes';

interface NumberInputProps extends StringInputProps {
    isPositive: boolean;
}

export const NumberInput = ({
    column,
    value,
    handleMouseDown,
    handleChange,
    handleKeyDown,
    inputRef,
    searchMode,
    isReadonly,
    onFocus,
    isPositive,
    onBlur
}: NumberInputProps) => {
    const maxNumberLength = useMemo(() => {
        const {maxLength, decimal} = column;
        return getMaxNumberLength({maxLength, decimal});
    }, [column]);

    const validateInput = (inputValue: string) => {
        let stringToValidate = inputValue;
        if (inputValue.length && column.decimal && inputValue[0] === getDecimalSign()) {
            stringToValidate = `0${inputValue}`;
        }
        return isValid(
            stringToValidate,
            column.decimal,
            maxNumberLength,
            isPositive,
            getThousandSign()
        );
    };

    const validateAndChange = (inputValue: string) => {
        if (searchMode) {
            handleChange(inputValue);
        } else if (validateInput(inputValue)) {
            let newValue = inputValue;
            if (column.decimal) {
                const integerPartLength = maxNumberLength - column.decimal;
                if (inputValue.length === integerPartLength && inputValue.length > value.length) {
                    newValue = newValue + getDecimalSign();
                } else if (
                    inputValue.length === integerPartLength + 1 &&
                    inputValue[inputValue.length - 1] !== getDecimalSign()
                ) {
                    newValue =
                        inputValue.slice(0, inputValue.length - 1) +
                        getDecimalSign() +
                        inputValue.slice(inputValue.length - 1);
                }
            }
            handleChange(newValue);
        }
    };

    const processKeyDown = (event: KeyboardEvent) => {
        if (!isReadonly) {
            if (event.key === keys.MINUS || event.key === keys.PLUS) {
                event.preventDefault();
                let number;
                if (!value && event.key === keys.MINUS) {
                    validateAndChange('-');
                } else {
                    if (value === '-') {
                        if (event.key === keys.MINUS) {
                            number = -1;
                        } else {
                            number = 0;
                        }
                        validateAndChange(number);
                    } else {
                        number = value ? strToFloat(value, getThousandSign()) : 0;
                        const currentDecimal = countDecimals(number);
                        number = number + (event.key === keys.PLUS ? 1 : -1);
                        validateAndChange(number.toFixed(currentDecimal));
                    }
                }
            }
        }
        handleKeyDown(event);
    };

    return (
        <input
            type="text"
            maxLength={column.maxLength}
            ref={inputRef}
            value={value}
            onKeyDown={processKeyDown}
            onChange={e => validateAndChange(e.target.value)}
            readOnly={isReadonly}
            onMouseDown={handleMouseDown}
            className={direction.ltrDirection}
            onFocus={onFocus}
            onBlur={onBlur}
        />
    );
};
